import { Form, Formik } from "formik";
import React, { FC, ReactElement, useEffect, useState } from "react";
import Modal from "react-modal";
import { useSelector } from "react-redux";
import { EncryptionTypes } from "../../../constants/encryptionTypes";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { VALIDITY_IN_OPTION } from "../../../models/CertificateType";
import WizardSideBarStep from "../../../models/WizardSideBarStep";
import { generateCertificate } from "../../../services/securityPolicyService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { buildCertForGeneration } from "../../../utilities/buildCertForGeneration";
import WizardTwo from "../../wizard/wizardTwo";
import CertificateDetails from "./CertificateDetails";
import Encryption from "./Encryption";
import SubjectAlternativeName from "./SubjectAlternativeName";
import Summary from "./Summary";
import CertificateValidity from "./CertificateValidity";
import { useDialog } from "../../dialog/DialogContext";

interface GenerateCertificateWizardProps {
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setBusy: React.Dispatch<React.SetStateAction<boolean>>;
    certificateDetails: any;
}

const GenerateCertificateWizard: FC<GenerateCertificateWizardProps> = ({ isOpen, setIsOpen, setBusy, certificateDetails }) => {
    const [step, setStep] = useState(0);
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const dialog = useDialog();
    const [stepsDescription, setStepsDescription] = useState<{ details: string, encryption: string, subjectAlternativeName: string }>({
        details: translate.byKey(TranslationKeys.pending),
        encryption: translate.byKey(TranslationKeys.pending),
        subjectAlternativeName: translate.byKey(TranslationKeys.pending)
    });
    const initialValues = {
        id: 0,
        certificateTypeId: '',
        certificateTypeLabel: '',
        alias: '',
        commonName: '',
        validityIn: VALIDITY_IN_OPTION.YEARS,
        validityInLabel: translate.byKey(TranslationKeys.years),
        validFor: "1",
        encryptionType: EncryptionTypes.RSA,
        shaAlgorithm: '',
        isCA: false,
        certificateAuthorityId: '',
        certificateAuthorityLabel: '',
        domainNameSystem: '',
        internetProtocol: '',
        uniformResourceIdentifier: ''
    }
    const [certificate, setCertitificate] = useState(initialValues);

    useEffect(() => {
        if (certificateDetails) {
            setCertitificate(certificateDetails);
        }
    }, [certificateDetails]);

    const steps: WizardSideBarStep[] = [
        {
            title: translate.byKey(TranslationKeys.details),
            description: stepsDescription.details,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.encryption),
            description: stepsDescription.encryption,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.subjectAlternativeName),
            description: stepsDescription.subjectAlternativeName,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        }
    ];

    const recreatedSteps: WizardSideBarStep[]  = [
        {
            title: translate.byKey(TranslationKeys.certificateValidity),
            description: stepsDescription.details,
            substeps: []
        },
        {
            title: translate.byKey(TranslationKeys.summary),
            description: translate.byKey(TranslationKeys.pending),
            substeps: []
        }
    ];

    const onNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setStep((prevStep) => prevStep + 1);
    }

    const onBack = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setStep((prevStep) => prevStep - 1);
    }

    const stepContent: ReactElement[] = [
        <CertificateDetails key={"certDetails"} onNext={onNext} setStepsDescription={setStepsDescription} />,
        <Encryption key={"certEncryption"} onNext={onNext} onBack={onBack} setStepsDescription={setStepsDescription} />,
        <SubjectAlternativeName key={"certSubjectAlternativeName"} onNext={onNext} onBack={onBack} setStepsDescription={setStepsDescription} />,
        <Summary key={"certSummary"} onBack={onBack} />
    ];

    const recreatedStepContent: ReactElement[] = [
        <CertificateValidity key={"certValidity"} onNext={onNext} setStepsDescription={setStepsDescription} />,
        <Summary key={"certSummary"} onBack={onBack} />
    ];

    const onSubmit = async (values) => {
        try {
            setBusy(true);
            const certificate = buildCertForGeneration(values, values.id, siteId, user.id);
            await generateCertificate(certificate);
        } catch (error) {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        } finally {
            onClose();
            setBusy(false);
        }
    }

    const onClose = () => {
        setIsOpen(false);
        setStep(0);
        setCertitificate(initialValues);
    }

    return <Modal
        isOpen={isOpen}
        ariaHideApp={false}
        overlayClassName={`flex w-screen position-float fixed top-0 left-0 h-screen bg-opacity-75 bg-white`}
        className={`bg-white border-none m-auto shadow select-none outline-none w-content `}>
        <Formik
            enableReinitialize={true}
            initialValues={certificate}
            onSubmit={onSubmit}>
            <Form>
                <WizardTwo
                    name={certificate.id ? translate.byKey(TranslationKeys.recreateCertificate) : translate.byKey(TranslationKeys.generateCertificate)}
                    step={step}
                    onClose={onClose}
                    sidebarSteps={certificate.id ? recreatedSteps : steps}
                    stepContent={certificate.id ? recreatedStepContent : stepContent} />
            </Form>
        </Formik>
    </Modal>
}

export default GenerateCertificateWizard;